import { Button } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { motion } from "framer-motion";
import { Award, BarChart2, Play, Rocket, Target } from "lucide-react";
import { Link } from "react-router";

import { useAuthStore } from "@/client/stores/auth";

import { Highlight } from "../../components/highlight";
import { Decoration } from "./decoration";

export const HeroSection = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);

  return (
    <section id="hero" className="relative overflow-hidden">
      <Decoration.Grid />
      <Decoration.Gradient />

      <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-center px-6 pt-36 text-center sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{
            opacity: 1,
            scale: 1,
            transition: {
              duration: 0.6,
              ease: "easeOut",
            },
          }}
          className="space-y-6"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                delay: 0.2,
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
          >
            <h1 className="mx-auto max-w-5xl text-center text-4xl font-black leading-tight text-neutral-800 dark:text-neutral-200 md:text-5xl lg:text-6xl">
              <span className="inline-block">{t`Your entire job search,`}</span>{" "}
              <Highlight className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t`run by AI`}
              </Highlight>
            </h1>
          </motion.div>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                delay: 0.4,
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
            className="mx-auto max-w-3xl text-lg text-neutral-600 dark:text-neutral-400 md:text-xl"
          >
            {t`Izicvs finds the jobs, ranks them for your resume, tailors your CV and cover letter, prepares each application and tracks it all, from search to interview.`}
            <span className="mt-2 block text-sm text-neutral-500 dark:text-neutral-500">
              {t`Stop applying blind. Apply smart.`}
            </span>
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                delay: 0.6,
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            {isLoggedIn ? (
              <Link to="/dashboard" className="w-full sm:w-auto">
                <Button size="lg" className="group w-full sm:w-auto">
                  <Rocket className="mr-2 size-5 group-hover:animate-bounce" />
                  <Link to="/dashboard">{t`Dashboard`}</Link>
                </Button>
              </Link>
            ) : (
              <Link to="/auth/register" className="w-full sm:w-auto">
                <Button size="lg" className="group w-full sm:w-auto">
                  <Rocket className="mr-2 size-5 group-hover:animate-bounce" />
                  {t`Start for free`}
                </Button>
              </Link>
            )}

            <Button variant="outline" size="lg" className="group w-full sm:w-auto">
              <Play className="mr-2 size-5 text-neutral-500 group-hover:text-neutral-800 dark:group-hover:text-neutral-200" />
              {t`See how it works`}
            </Button>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{
              y: 0,
              opacity: 1,
              transition: {
                delay: 0.8,
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
            className="mt-12 flex items-center justify-center space-x-8 text-neutral-500 dark:text-neutral-600"
          >
            <div className="flex items-center">
              <Target className="mr-2 size-6" />
              <span className="text-sm">{t`Jobs ranked for you`}</span>
            </div>
            <div className="flex items-center">
              <Award className="mr-2 size-6" />
              <span className="text-sm">{t`Official ATS sources`}</span>
            </div>
            <div className="flex items-center">
              <BarChart2 className="mr-2 size-6" />
              <span className="text-sm">{t`One-click apply`}</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
